import { Link, usePage } from '@inertiajs/react';
import { Head } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  MapPin, Award, Languages, Calendar, Tag, Briefcase, 
  Building, AccessibilityIcon, Dumbbell, Globe, 
  Clock, Users, Car, UserCheck, Timer, CreditCard,
  MessageSquare, ExternalLink, Star, Edit, X, CheckCircle, FileText
} from 'lucide-react';
import { router } from '@inertiajs/react';

interface MetricsProps {
  total_views: number;
  views_last_7_days: number;
}

interface Physio {
  id: string;
  firstName: string;
  lastName: string;
  address: string | null;
  city: string | null;
  phone: string | null;
  specialties: string[];
  body_zones: string[];
  accessibility: string[];
  languages: string[];
  equipment: string[];
  payment_type: string | null;
  appointment_mode: string | null;
  booking_link: string | null;
  profile_complete: boolean;
  // Tous les autres champs disponibles
  practice_locations?: string[];
  home_visit_areas?: string;
  professional_address?: string;
  practice_name?: string;
  practitioners_count?: number;
  parking_type?: string;
  parking_types?: string[];
  professional_email?: string;
  professional_website?: string;
  preferred_contact?: string;
  preferred_contact_details?: string;
  appointment_methods?: string[];
  online_booking_link?: string;
  secretariat_hours?: string;
  new_patients_policy?: string;
  session_duration?: string;
  patient_types?: string[];
  waiting_list?: boolean;
  waiting_list_delay?: string;
  practice_mode?: string;
  practice_modes?: string[];
  honorary_details?: string;
  payment_methods?: string[];
  coordinated_practice?: string[];
  coordinated_practice_details?: string;
  techniques?: string[];
  other_techniques?: string;
  sports?: string[];
  secondary_address?: string;
  cv_url?: string;
  diploma_year?: number;
  diploma_place?: string;
  diploma_extra_1?: string;
  diploma_extra_2?: string;
  other_activities?: string[];
  other_activities_details?: string;
  association_name?: string;
  rpps_number?: string;
  adeli_number?: string;
  publications?: string;
  other_equipment?: string;
  other_languages?: string;
  other_sports?: string;
  approved_specialties?: string[];
  page_views_count?: number;
  unique_visitors_count?: number;
  lat?: number;
  lng?: number;
  isCertified?: boolean;
}

interface QuestionnaireRequest {
  id: number;
  specialty_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface Props {
  physio: Physio;
  metrics?: MetricsProps;
  questionnaire_requests?: QuestionnaireRequest[];
}

// Fonction pour formater les langues
const formatLanguage = (lang: string) => {
  const mapping: Record<string, string> = {
    "fr": "Français",
    "en": "Anglais",
    "es": "Espagnol",
    "de": "Allemand",
    "it": "Italien",
    "ar": "Arabe"
  };
  return mapping[lang] || lang;
};

// Fonction pour formater les accessibilités
const formatAccessibility = (access: string) => {
  const mapping: Record<string, string> = {
    "wheelchair": "Accès fauteuil roulant",
    "parking": "Parking",
    "elevator": "Ascenseur",
    "wideAccess": "Accès large",
    "rez-de-chaussée": "Rez-de-chaussée"
  };
  return mapping[access] || access;
};

export default function Dashboard({ physio, metrics, questionnaire_requests = [] }: Props) {
  const { flash } = usePage().props as any;
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');

  useEffect(() => {
    if (flash?.notice) {
      setAlertMessage(flash.notice);
      setShowAlert(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [flash?.notice]);

  const {
    firstName,
    lastName,
    address,
    city,
    phone,
    specialties = [],
    body_zones = [],
    accessibility = [],
    languages = [],
    equipment = [],
    payment_type,
    appointment_mode,
    booking_link,
    practice_locations = [],
    home_visit_areas,
    professional_address,
    practice_name,
    practitioners_count,
    parking_type,
    parking_types = [],
    professional_email,
    professional_website,
    preferred_contact,
    preferred_contact_details,
    appointment_methods = [],
    online_booking_link,
    secretariat_hours,
    new_patients_policy,
    session_duration,
    patient_types = [],
    waiting_list,
    waiting_list_delay,
    practice_mode,
    practice_modes = [],
    honorary_details,
    payment_methods = [],
    coordinated_practice = [],
    coordinated_practice_details,
    techniques = [],
    other_techniques,
    sports = [],
    secondary_address,
    cv_url,
    diploma_year,
    diploma_place,
    diploma_extra_1,
    diploma_extra_2,
    other_activities = [],
    other_activities_details,
    association_name,
    rpps_number,
    adeli_number,
    publications,
    other_equipment,
    other_languages,
    other_sports,
    approved_specialties = [],
    unique_visitors_count = 0
  } = physio;

  // Regrouper les zones du corps pour éviter les doublons
  const formatBodyZone = (zone: string) => {
    const mapping: Record<string, string> = {
      "head": "Tête",
      "neck": "Cou",
      "torso": "Torse",
      "pelvis": "Bassin",
      "left-arm": "Bras",
      "right-arm": "Bras",
      "left-hand": "Mains",
      "right-hand": "Mains",
      "left-leg": "Jambes",
      "right-leg": "Jambes",
      "left-foot": "Pieds",
      "right-foot": "Pieds"
    };
    return mapping[zone] || zone;
  };
  const uniqueBodyZones = [...new Set(body_zones.map(zone => formatBodyZone(zone)))];

  return (
    <>
      <Head title="Mon Profil - Espace Personnel" />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Alert Message */}
          {showAlert && alertMessage && (
            <Alert className="mb-6 bg-green-50 border-green-200 text-green-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <AlertDescription>{alertMessage}</AlertDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAlert(false)}
                  className="h-6 w-6 p-0 text-green-800 hover:text-green-900"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </Alert>
          )}

          {/* En-tête */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {firstName} {lastName}
              </h1>
              {practice_name && (
                <p className="text-lg text-gray-600">{practice_name}</p>
              )}
              <div className="mt-2 flex items-center text-gray-500">
                <MapPin className="h-4 w-4 mr-1.5" />
                <span>
                  {address}
                  {secondary_address ? `, ${secondary_address}` : ''}
                  {city && `, ${city}`}
                </span>
              </div>
            </div>
            <Link href={`/physiotherapists/${physio.id}/edit`}>
              <Button>
                <Edit className="h-4 w-4 mr-2" />
                Modifier mon profil
              </Button>
            </Link>
          </div>

          {/* Section métriques */}
          {metrics && (
            <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 text-center">
                <div className="text-sm text-gray-500">Vues totales</div>
                <div className="text-2xl font-bold">{metrics.total_views}</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-sm text-gray-500">Vues (7 derniers jours)</div>
                <div className="text-2xl font-bold">{metrics.views_last_7_days}</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-sm text-gray-500">Visiteurs uniques</div>
                <div className="text-2xl font-bold">{unique_visitors_count}</div>
              </Card>
            </div>
          )}

          {/* Informations principales */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Informations personnelles */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-[#008788]" />
                Informations Personnelles
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Nom complet</label>
                  <p className="font-medium">{firstName} {lastName}</p>
                </div>
                {phone && (
                  <div>
                    <label className="text-sm text-gray-500">Téléphone</label>
                    <p className="font-medium">{phone}</p>
                  </div>
                )}
                {professional_email && (
                  <div>
                    <label className="text-sm text-gray-500">Email professionnel : </label>
                    <a href={`mailto:${professional_email}`} className="font-medium text-[#008788] hover:underline">
                      {professional_email}
                    </a>
                  </div>
                )}
                {address && (
                  <div>
                    <label className="text-sm text-gray-500">Adresse</label>
                    <p className="font-medium">{address}</p>
                  </div>
                )}
                {city && (
                  <div>
                    <label className="text-sm text-gray-500">Ville</label>
                    <p className="font-medium">{city}</p>
                  </div>
                )}
                {secondary_address && (
                  <div>
                    <label className="text-sm text-gray-500">Adresse secondaire</label>
                    <p className="font-medium">{secondary_address}</p>
                  </div>
                )}
                {rpps_number && (
                  <div>
                    <label className="text-sm text-gray-500">Numéro RPPS</label>
                    <p className="font-medium">{rpps_number}</p>
                  </div>
                )}
                {adeli_number && (
                  <div>
                    <label className="text-sm text-gray-500">Numéro ADELI</label>
                    <p className="font-medium">{adeli_number}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Spécialités */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Award className="h-5 w-5 mr-2 text-[#008788]" />
                Spécialités
              </h2>
              <div className="space-y-4">
                {specialties.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {specialties.map((specialty, index) => {
                        const isApproved = approved_specialties.includes(specialty);
                        const request = questionnaire_requests.find(r => r.specialty_name === specialty);
                        const canRequest = !isApproved && !request;
                        const requestStatus = request?.status;
                        
                        return (
                          <div key={index} className="flex items-center gap-2">
                            <Badge 
                              variant="secondary"
                              className={isApproved ? 'bg-amber-100 text-amber-800 border border-amber-300' : ''}
                            >
                              {isApproved && <Star className="h-3 w-3 mr-1 fill-amber-500 text-amber-500" />}
                              {specialty}
                              {isApproved && <span className="text-xs ml-1">(Approuvé)</span>}
                            </Badge>
                            {canRequest && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  if (confirm(`Demander un questionnaire pour la spécialité "${specialty}" ?`)) {
                                    router.post('/questionnaire_requests', {
                                      specialty_name: specialty
                                    });
                                  }
                                }}
                                className="text-xs"
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Demander un questionnaire
                              </Button>
                            )}
                            {requestStatus === 'pending' && (
                              <span className="text-xs text-gray-500">Demande en attente de traitement</span>
                            )}
                            {requestStatus === 'rejected' && (
                              <span className="text-xs text-red-600">Rejeté</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucune spécialité renseignée</p>
                )}
                {body_zones.length > 0 && (
                  <div>
                    <label className="text-sm text-gray-500 block mb-2">Zones du corps traitées</label>
                    <div className="flex flex-wrap gap-2">
                      {uniqueBodyZones.map((zone, index) => (
                        <Badge key={index} variant="outline">{zone}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* CV & Diplômes */}
            {(cv_url || diploma_year || diploma_place || diploma_extra_1 || diploma_extra_2) && (
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-[#008788]" />
                  CV & Diplômes
                </h2>
                <div className="space-y-3">
                  {cv_url && (
                    <div>
                      <a href={cv_url} target="_blank" rel="noopener noreferrer" className="text-[#008788] hover:underline flex items-center">
                        Voir le CV
                        <ExternalLink className="h-3.5 w-3.5 ml-1" />
                      </a>
                    </div>
                  )}
                  {(diploma_year || diploma_place) && (
                    <div>
                      <label className="text-sm text-gray-500">Diplôme</label>
                      <p className="font-medium">
                        {diploma_year ? `${diploma_year}` : ''}
                        {diploma_place ? `, ${diploma_place}` : ''}
                      </p>
                    </div>
                  )}
                  {diploma_extra_1 && (
                    <div>
                      <label className="text-sm text-gray-500">Formation complémentaire</label>
                      <p className="font-medium">{diploma_extra_1}</p>
                    </div>
                  )}
                  {diploma_extra_2 && (
                    <div>
                      <label className="text-sm text-gray-500">Formation complémentaire</label>
                      <p className="font-medium">{diploma_extra_2}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Informations complémentaires */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold mb-6">Informations Complémentaires</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Lieu d'exercice */}
              {practice_locations.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Building className="h-4 w-4 mr-1.5 text-gray-500" />
                    Lieu d'exercice
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {practice_locations.map((location, index) => (
                      <Badge key={index} variant="outline">{location}</Badge>
                    ))}
                  </div>
                  {home_visit_areas && practice_locations.includes("Domicile") && (
                    <p className="text-sm text-gray-600 mt-2">{home_visit_areas}</p>
                  )}
                </div>
              )}

              {/* Type de patientèle */}
              {patient_types.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-1.5 text-gray-500" />
                    Type de patientèle
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {patient_types.map((type, index) => (
                      <Badge key={index} variant="outline">{type}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Techniques */}
              {techniques.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Briefcase className="h-4 w-4 mr-1.5 text-gray-500" />
                    Techniques
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {techniques.map((technique, index) => (
                      <Badge key={index} variant="outline">{technique}</Badge>
                    ))}
                  </div>
                  {other_techniques && (
                    <p className="text-sm text-gray-600 mt-2">{other_techniques}</p>
                  )}
                </div>
              )}

              {/* Équipements */}
              {equipment.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Dumbbell className="h-4 w-4 mr-1.5 text-gray-500" />
                    Équipements
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {equipment.map((item, index) => (
                      <Badge key={index} variant="outline">{item}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Sports */}
              {sports.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Dumbbell className="h-4 w-4 mr-1.5 text-gray-500" />
                    Sports
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {sports.map((sport, index) => (
                      <Badge key={index} variant="outline">{sport}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Accessibilité */}
              {accessibility.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <AccessibilityIcon className="h-4 w-4 mr-1.5 text-gray-500" />
                    Accessibilité
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {accessibility.map((item, index) => (
                      <Badge key={index} variant="outline">{formatAccessibility(item)}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Langues parlées */}
              {languages.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Languages className="h-4 w-4 mr-1.5 text-gray-500" />
                    Langues parlées
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {languages.map((lang, index) => (
                      <Badge key={index} variant="outline">{formatLanguage(lang)}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Stationnement */}
              {(parking_type || parking_types.length > 0) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Car className="h-4 w-4 mr-1.5 text-gray-500" />
                    Stationnement
                  </h3>
                  {parking_types.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {parking_types.map((t, idx) => (
                        <Badge key={idx} variant="outline">{t}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{parking_type}</p>
                  )}
                </div>
              )}

              {/* Nouveaux patients */}
              {new_patients_policy && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <UserCheck className="h-4 w-4 mr-1.5 text-gray-500" />
                    Nouveaux patients
                  </h3>
                  <p className="text-gray-600">{new_patients_policy}</p>
                </div>
              )}

              {/* Durée des séances */}
              {session_duration && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Timer className="h-4 w-4 mr-1.5 text-gray-500" />
                    Durée des séances
                  </h3>
                  <p className="text-gray-600">{session_duration}</p>
                </div>
              )}

              {/* Liste d'attente */}
              {waiting_list !== undefined && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-1.5 text-gray-500" />
                    Liste d'attente
                  </h3>
                  <p className="text-gray-600">
                    {waiting_list ? `Oui${waiting_list_delay ? ` (${waiting_list_delay})` : ''}` : 'Non'}
                  </p>
                </div>
              )}

              {/* Mode d'exercice */}
              {(practice_mode || practice_modes.length > 0) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Building className="h-4 w-4 mr-1.5 text-gray-500" />
                    Mode d'exercice
                  </h3>
                  {practice_modes.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {practice_modes.map((m, idx) => (
                        <Badge key={idx} variant="outline">{m}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{practice_mode}</p>
                  )}
                  {honorary_details && (
                    <p className="text-sm text-gray-500 mt-1">{honorary_details}</p>
                  )}
                </div>
              )}

              {/* Activités autres */}
              {(other_activities.length > 0 || other_activities_details) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Tag className="h-4 w-4 mr-1.5 text-gray-500" />
                    Activités autres
                  </h3>
                  {other_activities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {other_activities.map((a, idx) => (
                        <Badge key={idx} variant="outline">{a}</Badge>
                      ))}
                    </div>
                  )}
                  {other_activities_details && (
                    <p className="text-gray-600 mt-2">{other_activities_details}</p>
                  )}
                </div>
              )}

              {/* Association */}
              {association_name && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Tag className="h-4 w-4 mr-1.5 text-gray-500" />
                    Association
                  </h3>
                  <p className="text-gray-600">{association_name}</p>
                </div>
              )}

              {/* Modes de paiement */}
              {(payment_methods.length > 0 || payment_type) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <CreditCard className="h-4 w-4 mr-1.5 text-gray-500" />
                    Modes de paiement
                  </h3>
                  {payment_methods.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {payment_methods.map((method, index) => (
                        <Badge key={index} variant="outline">{method}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{payment_type}</p>
                  )}
                </div>
              )}

              {/* Exercice coordonné */}
              {coordinated_practice.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Building className="h-4 w-4 mr-1.5 text-gray-500" />
                    Exercice coordonné
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {coordinated_practice.map((practice, index) => (
                      <Badge key={index} variant="outline">{practice}</Badge>
                    ))}
                  </div>
                  {coordinated_practice_details && (
                    <p className="text-sm text-gray-500 mt-2">{coordinated_practice_details}</p>
                  )}
                </div>
              )}

              {/* Prise de rendez-vous */}
              {(appointment_methods.length > 0 || appointment_mode) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-1.5 text-gray-500" />
                    Prise de rendez-vous
                  </h3>
                  {appointment_methods.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {appointment_methods.map((method, index) => (
                        <Badge key={index} variant="outline">{method}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{appointment_mode}</p>
                  )}
                </div>
              )}

              {/* Horaires secrétariat */}
              {secretariat_hours && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-1.5 text-gray-500" />
                    Horaires secrétariat
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{secretariat_hours}</p>
                </div>
              )}

              {/* Contact préférentiel */}
              {preferred_contact && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-1.5 text-gray-500" />
                    Contact préférentiel
                  </h3>
                  <p className="text-gray-600">
                    {preferred_contact}
                    {preferred_contact_details && ` - ${preferred_contact_details}`}
                  </p>
                </div>
              )}

              {/* Site web */}
              {professional_website && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Globe className="h-4 w-4 mr-1.5 text-gray-500" />
                    Site web
                  </h3>
                  <a 
                    href={professional_website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#008788] hover:underline flex items-center gap-1"
                  >
                    {professional_website.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {/* Nombre de praticiens */}
              {practitioners_count && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-1.5 text-gray-500" />
                    Nombre de praticiens
                  </h3>
                  <p className="text-gray-600">{practitioners_count}</p>
                </div>
              )}

              {/* Publications */}
              {publications && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Tag className="h-4 w-4 mr-1.5 text-gray-500" />
                    Publications
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{publications}</p>
                </div>
              )}

              {/* Autres équipements */}
              {other_equipment && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Dumbbell className="h-4 w-4 mr-1.5 text-gray-500" />
                    Autres équipements
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{other_equipment}</p>
                </div>
              )}

              {/* Autres langues */}
              {other_languages && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Languages className="h-4 w-4 mr-1.5 text-gray-500" />
                    Autres langues
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{other_languages}</p>
                </div>
              )}

              {/* Autres sports */}
              {other_sports && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Dumbbell className="h-4 w-4 mr-1.5 text-gray-500" />
                    Autres sports
                  </h3>
                  <p className="text-gray-600 whitespace-pre-line">{other_sports}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Bouton de réservation */}
          {(booking_link || online_booking_link) && (
            <Card className="p-6 text-center">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Prendre rendez-vous en ligne</h2>
              <Button 
                onClick={() => window.open(online_booking_link || booking_link || '', "_blank")}
                className="bg-[#008788] hover:bg-[#00bcbe]"
                size="lg"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Réserver en ligne
              </Button>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
